import { DataLoadError } from '@creditsights/styleguide-web';
import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { MY_CONTENT_ROUTES } from '../../../Base.constants';
import useDefaultReadingListWithArticleTags from '../../../hooks/readingList/useDefaultReadingListWithArticleTags';
import { invalidateReadingListQueries } from '../../../hooks/readingList/utils/invalidateReadingListQueries';
import { sortArticles } from './ArticlesTab.logic';
import ArticlesTabContainer from './ArticlesTabContainer/ArticlesTabContainer';

const ArticlesTab = (): React.ReactElement => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const {
    data: defaultReadingList,
    isLoading: isDefaultReadingListLoading,
    isError,
  } = useDefaultReadingListWithArticleTags();
  const [isArticleInInitialList, setIsArticleInInitialList] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const activeArticlesIds = Number.isNaN(articleId ?? Number.NaN) ? undefined : [Number(articleId)];

  const sortedReadingListArticles = useMemo(
    () => (defaultReadingList ? Array.from(defaultReadingList?.articles.values()).sort(sortArticles) : undefined),
    [defaultReadingList]
  );

  useEffect(() => {
    if (!articleId && sortedReadingListArticles && sortedReadingListArticles.length) {
      navigate(MY_CONTENT_ROUTES.articles(sortedReadingListArticles[0].articleId, true), { replace: true });
    }
  }, [articleId, sortedReadingListArticles]);

  useMemo(() => {
    if (articleId && sortedReadingListArticles && !isArticleInInitialList) {
      setIsArticleInInitialList(sortedReadingListArticles.some((article) => article.articleId === Number(articleId)));
    }
  }, [articleId, sortedReadingListArticles]);

  if (isError) {
    return <DataLoadError onRetryClick={() => invalidateReadingListQueries(queryClient)} />;
  }

  return (
    <ArticlesTabContainer
      articles={sortedReadingListArticles}
      activeArticlesIds={activeArticlesIds}
      isArticleInInitialList={isArticleInInitialList}
      isAnyArticleLoading={isDefaultReadingListLoading}
    />
  );
};

export default ArticlesTab;
